import React from 'react';
import { FONT_SIZE } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { formatPrice } from '../../utils/format';

interface MarketRangeBarProps {
  /** 타 샵 시세 최저/최고가 (null이면 "참조 시세 없음"으로 표시). */
  marketMin:      number | null;
  marketMax:      number | null;
  /** 네이버 가격 — 회색 눈금. */
  currentPrice:   number | null;
  /** <매장> 가격(PriceHub) — 빨간 점. */
  suggestedPrice: number | null;
  /** 막대 영역 높이(px). 기본 20 — 테이블용. 모달에선 크게. */
  height?:   number;
  /** 좌우에 최저/최고가 라벨을 보여줄지. 켜면 아래에 범례도 붙는다. */
  showScale?: boolean;
  /** 범례 · 읽어 주는 설명의 눈금 · 점 이름(감사 #518). */
  currentLabel?:   string;
  suggestedLabel?: string;
}

/**
 * 타 샵 시세 범위 대비 네이버 가격 · <매장> 가격 위치를 한눈에 보여주는 미니 막대.
 * 초록 밴드 = 타 샵 시세 최저~최고, 회색 눈금 = 네이버 가격, 빨간 점 = <매장> 가격.
 * "하락 검토 개선안"(2026-08-27)에서 도입 — 의심 테이블과 하락 확인 모달이 공유한다.
 * 차트 라이브러리 없이 절대 위치 div로만 그린다.
 */
export const MarketRangeBar: React.FC<MarketRangeBarProps> = ({
  marketMin, marketMax, currentPrice, suggestedPrice, height = 20, showScale = false,
  // 기본 이름은 용어 사전(규칙 41)의 '매장 가격'(storePriceLabel(null) 과 같은 값) — '추천가' 는 #489 에서 걷어낸 말(감사 #645).
  // 매장을 아는 곳은 '<매장> 가격'(useStorePriceName)을 넘긴다.
  currentLabel = '네이버 가격', suggestedLabel = '매장 가격',
}) => {
  const t = useThemeTokens();

  const hasRange = marketMin != null && marketMax != null;
  if (!hasRange) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: FONT_SIZE.xs, color: t.textDisabled }}>참조 시세 없음</span>
      </div>
    );
  }

  const values = [marketMin, marketMax, currentPrice, suggestedPrice]
    .filter((v): v is number => v != null && v > 0);
  const rawLo = Math.min(...values);
  const rawHi = Math.max(...values);
  const span  = rawHi - rawLo || rawHi || 1;
  // 양 끝에 8% 여백을 둬 점/눈금이 잘리지 않게 한다.
  const lo = rawLo - span * 0.08;
  const hi = rawHi + span * 0.08;
  const pct = (v: number) => Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100));

  const mid = Math.round(height / 2);
  // 색 · 모양의 뜻을 글로도(코덱 화면 점검 2026-10-30 낮음, 감사 #518 — 초록 밴드 · 회색 눈금 · 빨간 점의 뜻이 주석에만 있었다).
  const summary = [
    `타 샵 시세 ${formatPrice(marketMin as number)} ~ ${formatPrice(marketMax as number)}`,
    currentPrice != null && currentPrice > 0 ? `${currentLabel} ${formatPrice(currentPrice)}` : null,
    suggestedPrice != null && suggestedPrice > 0 ? `${suggestedLabel} ${formatPrice(suggestedPrice)}` : null,
  ].filter(Boolean).join(', ');

  return (
    <div role="img" aria-label={summary} title={summary}>
      <div style={{ position: 'relative', height }}>
        {/* 기준선 */}
        <div style={{
          position: 'absolute', left: 0, right: 0, top: mid - 1, height: 2,
          background: t.border, borderRadius: 1,
        }} />
        {/* 타 샵 시세 밴드 */}
        <div style={{
          position: 'absolute',
          left: `${pct(marketMin as number)}%`,
          width: `${pct(marketMax as number) - pct(marketMin as number)}%`,
          top: mid - 4, height: 8,
          background: t.tintSuccess, border: `1px solid ${t.lineSuccess}`, borderRadius: 2,
        }} />
        {/* 네이버 가격 눈금 */}
        {currentPrice != null && currentPrice > 0 && (
          <div style={{
            position: 'absolute', left: `${pct(currentPrice)}%`, top: mid - 7,
            width: 2, height: 14, background: t.textMuted, transform: 'translateX(-1px)',
          }} />
        )}
        {/* <매장> 가격 점 */}
        {suggestedPrice != null && suggestedPrice > 0 && (
          <div style={{
            position: 'absolute', left: `${pct(suggestedPrice)}%`, top: mid - 5,
            width: 10, height: 10, borderRadius: '50%',
            background: t.dangerSolid, border: `2px solid ${t.bgSurface}`,
            boxShadow: `0 0 0 1px ${t.dangerSolid}`, transform: 'translateX(-5px)',
          }} />
        )}
      </div>
      {showScale && (
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: FONT_SIZE.xs, color: t.textMuted, marginTop: 2,
        }}>
          <span>{formatPrice(marketMin as number)}</span>
          <span>{formatPrice(marketMax as number)}</span>
        </div>
      )}
      {showScale && (
        <div aria-hidden data-testid="market-range-legend" style={{
          display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: FONT_SIZE.xs, color: t.textMuted, marginTop: 2,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 14, height: 8, background: t.tintSuccess, border: `1px solid ${t.lineSuccess}`, borderRadius: 2 }} />
            타 샵 시세 범위
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 2, height: 12, background: t.textMuted }} />
            {currentLabel}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.dangerSolid }} />
            {suggestedLabel}
          </span>
        </div>
      )}
    </div>
  );
};
